import {
  ChangeDetectionStrategy,
  Component,
  signal,
  effect,
  inject,
  Output,
  EventEmitter,
  Inject,
  PLATFORM_ID,
  OnDestroy,
} from '@angular/core';
import { SharedModule } from '../shared.module';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FooterComponent } from '../footer/footer.component';
import { Router } from '@angular/router';
import { MailServiceService } from '../../../app/core/service/mail/mail-service.service';
import { LocalStorageService } from '../../../app/core/service/localStorage/local-storage.service';
import { ChangeDetectorRef } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { LottieComponent } from 'ngx-lottie';
import { AnimationOptions } from 'ngx-lottie';
import { MailCacheService } from '../../core/service/mailCache/mail-cache.service';
import { DomainConfigService } from '../../core/service/domainConfig/domain-config.service';
import { MatDialog } from '@angular/material/dialog';
import { CreateMeetingEventComponent } from '../create-meeting-event/create-meeting-event.component';
import { Subscription } from 'rxjs';
import { TagService, Tag } from '../../core/service/mail/tag.service';
import { CreateTagDialogComponent } from '../../features/mailbox/components/create-tag-dialog/create-tag-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';


@Component({
  selector: 'app-sidebar-mailbox',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    FooterComponent,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    SharedModule,
    LottieComponent,
    CreateMeetingEventComponent
  ],
  templateUrl: './sidebar-mailbox.component.html',
  styleUrl: './sidebar-mailbox.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarMailboxComponent implements OnDestroy {
  brandLogo = './assets/images/logo.svg';
  brandLogoIcon = './assets/images/logo-icon.svg';
  @Output() onBoxSelected = new EventEmitter<any>();
  private readonly mailService = inject(MailServiceService);
  private readonly mailCacheService = inject(MailCacheService);
  private localStorageService = inject(LocalStorageService);
  private domainConfigService = inject(DomainConfigService);
  private readonly dialog = inject(MatDialog);
  public readonly tagService: TagService = inject(TagService);

  readonly panelOpenState = signal(false);
  private firstLoad = true;
  private isInitialDashboardLoad = true; // Track if this is initial page load
  dashboardData: any; // for "All Mails"
  dashboardDataMap: { [email: string]: any } = {}; // per account
  dashboardDataError: boolean = false; // track error for "All Mails"
  dashboardDataMapError: { [email: string]: boolean } = {}; // track error per account
  userAccounts: any[] = [];
  userDetails: any;
  universityIds: any;
  userType: any;

  // Store all mails email and domain for "All Mails" section
  allMailsEmail: string = '';
  allMailsDomain: string = '';

  allMailFolders = [
    {
      label: 'Inbox',
      countKey: 'totalInbox',
      type: [0, 5, 7],
      image: 'inbox',
      myMails: false,
    },
    {
      label: 'Personal Mail',
      countKey: 'totalMyMails',
      type: [0, 5, 7],
      image: 'inbox',
    },
    { label: 'Sent', countKey: null, type: [2], image: 'send' },
    { label: 'Drafts', countKey: 'totalDrafts', type: [8], image: 'drafts' },
    {
      label: 'Important',
      countKey: 'totalImportant',
      type: [4],
      image: 'important',
    },
    { label: 'Done', countKey: 'totalArchive', type: [1], image: 'done' },
    // { label: 'Trash', countKey: null, type: [0, 5, 7], image: 'inbox' },
    {
      label: 'Shared With Me',
      countKey: 'totalShared',
      type: [5],
      image: 'shared',
    },
    { label: 'Junk', countKey: 'totalJunk', type: [6], image: 'junk' },
  ];
  lottieOptions: AnimationOptions = {
    path: 'assets/lottie/three-dot.json',
    loop: true,
    autoplay: true
  };
  showMeetingEvent = false;
  isComposeOpen = false;
  private composeSub?: Subscription;

  selectedFolder: string = '';
  selectedEmail: string = '';
  selectedTagId: string = '';

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.setupDomainBranding();
    effect(() => {
      const sidebarData = this.mailService.refreshSidebar();
      if (sidebarData) {
        const email = sidebarData?.email;
        const userType = sidebarData?.userType;
        const domain = sidebarData?.domain;
        const universityIds = sidebarData?.universityIds;

        // Sidebar refresh triggered - mark as not initial load
        this.isInitialDashboardLoad = false;

        this.fetchUserDashboard(email, userType, domain, universityIds);
      }
    });
  }

  ngOnInit(): void {
    this.loadUserDashboard();
    // Track compose open/close state so we can prevent re-opening when already open
    this.composeSub = this.mailService.composeAction$.subscribe((data: any) => {
      if (data === 'close' || data?.mode === 'close') {
        this.isComposeOpen = false;
      } else {
        this.isComposeOpen = true;
      }
    });
  }

  ngOnDestroy(): void {
    this.composeSub?.unsubscribe();
  }

  private async loadUserDashboard(): Promise<void> {
    try {
      const isSwitchUser = JSON.parse(
        this.localStorageService.getKey('isSwitchUser') || 'false'
      );
      const storageKey = isSwitchUser ? 'switchUser' : 'currentUser';
      this.userDetails = await this.localStorageService.getKeyIndexedDbKey(
        storageKey
      );

      if (this.userDetails?.inboxMail?.length > 0) {
        const emails = [
          ...new Set(
            this.userDetails.inboxMail
              .map((m: any) => m.mail?.trim())
              .filter((mail: string) => mail)
          ),
        ].join(',');
        this.userType = this.userDetails?.universitiesURM
          ? 'urm'
          : this.userDetails?.caseOwner
            ? 'caseOwner'
            : '';
        const domains = [
          ...new Set(this.userDetails.inboxMail.map((m: any) => m.domain)),
        ].join(',');
        this.universityIds = [
          ...new Set(this.userDetails.universityIds.map((m: any) => m)),
        ].join(',');

        // Store all mails email and domain for "All Mails" section
        this.allMailsEmail = emails;
        this.allMailsDomain = domains;
        console.log('📧 Stored All Mails - Email:', this.allMailsEmail);
        console.log('🌐 Stored All Mails - Domain:', this.allMailsDomain);

        this.userAccounts = this.userDetails?.inboxMail ?? [];
        /* making indivisual API call to get mails list */
        this.callMailLists(domains, emails, this.userType);

        this.fetchUserDashboard(
          emails,
          this.userType,
          domains,
          this.universityIds
        );
      }
    } catch (error) {
      console.error('Error loading user dashboard:', error);
    }
  }

  async fetchUserDashboard(
    email?: string,
    userType?: any,
    domain?: any,
    universityIds?: any,
    myMails?: any
  ): Promise<void> {
    const myMail = myMails ? JSON.parse(myMails) : '';
    const key = email || 'unknown';

    // Try to load from cache first (only on initial load)
    if (this.isInitialDashboardLoad && isPlatformBrowser(this.platformId)) {
      try {
        const hasCachedDashboard = await this.mailCacheService.hasCachedDashboard(key);

        if (hasCachedDashboard) {
          const cachedDashboard = await this.mailCacheService.getCachedDashboard(key);

          if (cachedDashboard) {
            // Load from cache
            if (email && email.includes(',')) {
              this.dashboardData = cachedDashboard;
            }
            this.dashboardDataMap[key] = cachedDashboard;
            this.cdr.markForCheck();
            console.log('✅ Loaded dashboard from cache:', key);

            // Call API in background to get fresh data
            this.fetchDashboardFromAPI(email, userType, domain, universityIds, myMail, key, true);

            return; // Exit early - show cached data
          }
        }
      } catch (error) {
        console.error('Dashboard cache read failed, falling back to API:', error);
      }
    }

    // Not initial load OR no cache - call API
    this.fetchDashboardFromAPI(email, userType, domain, universityIds, myMail, key, false);
  }

  private fetchDashboardFromAPI(
    email?: string,
    userType?: any,
    domain?: any,
    universityIds?: any,
    myMail?: any,
    key?: string,
    isBackgroundFetch: boolean = false
  ): void {
    if (isBackgroundFetch) {
      console.log('🔄 Fetching fresh dashboard data in background...');
    }

    this.mailService
      .fetchDashboardData(email, userType, domain, universityIds, myMail)
      .subscribe({
        next: async (res: any) => {
          const data = res?.data?.[0];
          if (!data) return;

          const emailLength = data.email.split(',').length;
          if (emailLength <= 0) return;

          if (email && email.includes(',')) {
            this.dashboardData = data;
          }

          const cacheKey = key || email || 'unknown';
          this.dashboardDataMap[cacheKey] = data;

          // Store in cache
          if (isPlatformBrowser(this.platformId)) {
            await this.mailCacheService.storeDashboard(cacheKey, data);

            if (isBackgroundFetch) {
              console.log('💾 Updated dashboard cache with fresh data in background');
              this.isInitialDashboardLoad = false;
            } else if (this.isInitialDashboardLoad) {
              console.log('💾 Stored dashboard in cache:', cacheKey);
              this.isInitialDashboardLoad = false;
            } else {
              console.log('🔄 Updated dashboard cache:', cacheKey);
            }
          }

          this.cdr.markForCheck();
        },
        error: (err: any) => {
          console.error('Dashboard fetch failed:', err);

          if (!isBackgroundFetch) {
            const errorKey = key || email || 'unknown';

            // Check if this is for "All Mails" (multiple emails) or individual account
            if (email && email.includes(',')) {
              // This is for "All Mails"
              this.dashboardDataError = true;
            } else {
              // This is for individual account
              this.dashboardDataMapError[errorKey] = true;
            }
          }

          // Mark initial load as complete even on error
          if (isBackgroundFetch) {
            this.isInitialDashboardLoad = false;
          }

          this.cdr.markForCheck();
        },
      });
  }

  onPanelOpened(account: string, caseOwner: any, domain: string, myMails: any) {
    // User clicked sidebar panel - mark as not initial load
    this.isInitialDashboardLoad = false;

    if (!this.dashboardDataMap[account]) {
      this.fetchUserDashboard(account, caseOwner, domain, false, myMails);
    }
  }

  goToMail(
    folder?: string,
    type?: any,
    domain?: string,
    email?: string,
    userType?: any,
    myMail?: any
  ) {
    this.selectedFolder = folder || '';
    this.selectedEmail = email || '';
    this.selectedTagId = '';

    const isPersonalAccount = myMail === 'true' || myMail === true;
    const account = this.userAccounts.find(acc => acc.mail === email);

    const mailData = {
      label: folder,
      ids: type,
      domain: domain,
      email: email,
      userType: userType,
      myMails: !!(isPersonalAccount && userType && folder === 'Personal Mail'),
      isPersonal: isPersonalAccount, // Pass the isPersonal flag
      createFolderAccess: (account?.createFolderAccess === 'true' || account?.createFolderAccess === true), // Check access
      universityIds: userType ? this.universityIds : '',
    };

    console.log('📤 Emitting mailData:', mailData);
    this.onBoxSelected.emit(mailData);
  }

  get personalAccounts() {
    return this.userAccounts.filter(acc =>
      (acc.createFolderAccess === 'true' || acc.createFolderAccess === true)
    );
  }

  get hasPersonalAccount() {
    return this.personalAccounts.length > 0;
  }

  getAccountTags(mailId: string): Tag[] {
    return this.tagService.getTagsForAccount(mailId);
  }

  createNewTag(mailId: string) {
    const dialogRef = this.dialog.open(CreateTagDialogComponent, {
      width: '400px',
      autoFocus: true,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(tagName => {
      if (tagName) {
        this.tagService.createTag(tagName, mailId);
        this.cdr.markForCheck();
      }
    });
  }

  goToTag(tag: Tag, mailId: string) {
    this.selectedFolder = '';
    this.selectedEmail = mailId;
    this.selectedTagId = tag.id;

    const account = this.userAccounts.find(acc => acc.mail === mailId);
    const mailData = {
      label: `Tag: ${tag.name}`,
      tagId: tag.id,
      domain: account?.domain,
      email: mailId,
      userType: this.userType,
      isPersonal: true,
      createFolderAccess: true, // If they can see the tag in sidebar, they have access
      universityIds: this.userType ? this.universityIds : '',
      // We'll use a special type or filter in MailListComponent to handle this
      isTagView: true
    };
    this.onBoxSelected.emit(mailData);
  }

  renameTag(event: MouseEvent, tag: Tag) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(CreateTagDialogComponent, {
      width: '400px',
      autoFocus: true,
      disableClose: true,
      data: { tagName: tag.name }
    });

    dialogRef.afterClosed().subscribe(newName => {
      if (newName && newName !== tag.name) {
        this.tagService.renameTag(tag.id, newName);
        this.cdr.markForCheck();
      }
    });
  }

  deleteTag(event: MouseEvent, tag: Tag) {
    event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        type: 'mailConfirmation',
        text: `Are you sure you want to delete the tag "${tag.name}"? This will remove the tag from all assigned emails.`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'update') {
        this.tagService.deleteTag(tag.id);
        this.cdr.markForCheck();
      }
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    const target = event.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  }

  onDragLeave(event: DragEvent) {
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  }

  async onDrop(event: DragEvent, tag: Tag) {
    event.preventDefault();
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('drag-over');

    try {
      const data = event.dataTransfer?.getData('application/json');
      if (data) {
        const mailIds = JSON.parse(data);
        if (Array.isArray(mailIds)) {
          console.log(`📦 Dropping ${mailIds.length} mails onto tag: ${tag.name}`);

          // Assign tag to each dropped mail
          for (const mailId of mailIds) {
            await this.tagService.assignTag(mailId, tag.id);
          }
        }
      }
    } catch (error) {
      console.error('❌ Drop failed:', error);
    }
  }


  openComposeMail() {
    if (this.isComposeOpen) return; // already open, do nothing
    if (this.showMeetingEvent) { this.showMeetingEvent = false; this.cdr.markForCheck(); }
    this.mailService.triggerCompose('compose');
  }

  callMailLists(domain: any, email: any, userType: any) {
    if (this.firstLoad) {
      this.firstLoad = false;
      if (this.userAccounts.length === 1) {
        this.goToMail(
          'Inbox',
          [0, 5, 7],
          this.userAccounts[0]?.domain,
          this.userAccounts[0]?.mail,
          userType,
          this.userAccounts[0]?.isPersonal
        );
      } else {
        this.goToMail(
          'Inbox',
          [0, 5, 7],
          domain,
          email,
          userType
        );
      }
    }
  }

  private setupDomainBranding(): void {
    this.brandLogo = this.domainConfigService.getDashboardLogo();
    this.brandLogoIcon = this.domainConfigService.getDashboardLogoIcon();
  }

  createMeetingEvent() {
    if (this.showMeetingEvent) return; // already open, do nothing
    this.mailService.triggerCompose('close');
    this.showMeetingEvent = true;
    this.cdr.markForCheck();
  }

  onMeetingEventSaved(result: any): void {
    this.showMeetingEvent = false;
  }
}